import { useEffect, useState, useRef, type MouseEvent } from "react";
import { Group } from "@visx/group";
import { LinePath, AreaClosed } from "@visx/shape";
import { scaleTime, scaleLinear } from "@visx/scale";
import { AxisLeft, AxisBottom } from "@visx/axis";
import { Tooltip, defaultStyles } from "@visx/tooltip";
import { useTooltip } from "@visx/tooltip";
import useDarkMode from "use-dark-mode";
import { HistoryStep } from "@/api/analytics";
import { stepToRub } from "./estimateUnits";

interface Props {
  steps: HistoryStep[];
  areaSqm: number;
  unitSuffix?: string;
  // Оценка из карточек (₽/мес, те же что сверху) — рисуется зоной на графике.
  estimateRub?: {
    low: number | null;
    median: number | null;
    high: number | null;
  };
}

interface Pt {
  date: Date;
  fact: number | null;
  estimateLow: number | null;
  estimate: number | null;
  estimateHigh: number | null;
}

const FACT = "#10B981"; // зелёный — ваша цена
const EST = "#3B82F6"; // синий — оценка (совпадает с карточкой «Оценка»)

export function EstimateChart({
  steps,
  areaSqm,
  unitSuffix,
  estimateRub,
}: Props) {
  const suffix = unitSuffix ?? "";
  const [width, setWidth] = useState(600);
  const ref = useRef<HTMLDivElement>(null);
  const dark = useDarkMode();
  const { tooltipData, tooltipLeft, tooltipTop, showTooltip, hideTooltip } =
    useTooltip<Pt>();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.offsetWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const height = 260;
  const m = { top: 20, right: 20, bottom: 50, left: 70 };
  const xMax = width - m.left - m.right;
  const yMax = height - m.top - m.bottom;

  const pts: Pt[] = steps
    .map((s) => ({ date: new Date(s.step_date), ...stepToRub(s, areaSqm) }))
    .filter((p) => p.fact !== null || p.estimate !== null);

  if (pts.length === 0) {
    return (
      <div className="flex items-center justify-center h-[260px] text-foreground-400">
        Нет данных
      </div>
    );
  }

  const dates = pts.map((p) => p.date);
  const estVals = estimateRub
    ? [estimateRub.low, estimateRub.median, estimateRub.high].filter(
        (v): v is number => v != null,
      )
    : [];
  const vals = [
    ...pts.flatMap((p) => [p.fact, p.estimateLow, p.estimate, p.estimateHigh]),
    ...estVals,
  ].filter((v): v is number => v != null);
  const yMin = Math.min(...vals) * 0.9;
  const yMaxVal = Math.max(...vals) * 1.1;

  const x = scaleTime({
    range: [0, xMax],
    domain: [Math.min(...dates.map(Number)), Math.max(...dates.map(Number))],
  });
  const y = scaleLinear({
    range: [yMax, 0],
    domain: [yMin, yMaxVal],
    round: true,
  });

  const axisColor = dark.value ? "#E5E7EB" : "#374151";
  const fmt = (v: { valueOf(): number } | number) =>
    new Intl.NumberFormat("ru-RU", {
      style: "currency",
      currency: "RUB",
      maximumFractionDigits: 0,
    }).format(Number(v)) + suffix;
  const fmtMaybe = (v: number | null) => (v == null ? "—" : fmt(v));

  const facts = pts.filter((p) => p.fact != null) as (Pt & { fact: number })[];
  const estimateCorridor = pts.filter(
    (p) => p.estimateLow != null && p.estimateHigh != null,
  ) as (Pt & { estimateLow: number; estimateHigh: number })[];
  const estimates = pts.filter((p) => p.estimate != null) as (Pt & {
    estimate: number;
  })[];
  const activeX = tooltipData ? x(tooltipData.date) : null;
  const forecastRange =
    tooltipData?.estimateLow != null && tooltipData.estimateHigh != null
      ? `${fmt(tooltipData.estimateLow)}–${fmt(tooltipData.estimateHigh)}`
      : "—";

  const handleMouseMove = (e: MouseEvent<SVGRectElement>) => {
    const bounds = e.currentTarget.getBoundingClientRect();
    const mouseX = Math.max(0, Math.min(xMax, e.clientX - bounds.left));
    const target = x.invert(mouseX).getTime();
    const nearest = pts.reduce((best, p) =>
      Math.abs(p.date.getTime() - target) <
      Math.abs(best.date.getTime() - target)
        ? p
        : best,
    );

    const anchorY = nearest.estimate ?? nearest.fact ?? yMin;
    showTooltip({
      tooltipLeft: Math.max(8, Math.min(width - 220, m.left + mouseX + 12)),
      tooltipTop: Math.max(8, m.top + y(anchorY) - 40),
      tooltipData: nearest,
    });
  };

  return (
    <div ref={ref} className="w-full">
      <svg width={width} height={height}>
        <Group left={m.left} top={m.top}>
          {/* Оценка полной модели */}
          {estimateCorridor.length > 1 && (
            <AreaClosed
              data={estimateCorridor}
              x={(d) => x(d.date)}
              y0={(d) => y(d.estimateHigh)}
              y1={(d) => y(d.estimateLow)}
              yScale={y}
              fill={EST}
              fillOpacity={0.12}
            />
          )}
          {estimates.length > 1 && (
            <LinePath
              data={estimates}
              x={(d) => x(d.date)}
              y={(d) => y(d.estimate)}
              stroke={EST}
              strokeWidth={2.5}
            />
          )}
          {estimates.map((d, i) => (
            <circle
              key={`estimate-${i}`}
              cx={x(d.date)}
              cy={y(d.estimate)}
              r={3}
              fill={EST}
            />
          ))}

          {estimates.length === 0 &&
            estimateRub &&
            estimateRub.low != null &&
            estimateRub.high != null &&
            estimateRub.median != null &&
            (() => {
              const xn = x(pts[pts.length - 1].date);
              const cap = 5;
              return (
                <Group>
                  <line
                    x1={xn}
                    x2={xn}
                    y1={y(estimateRub.high)}
                    y2={y(estimateRub.low)}
                    stroke={EST}
                    strokeWidth={2}
                  />
                  <line
                    x1={xn - cap}
                    x2={xn + cap}
                    y1={y(estimateRub.high)}
                    y2={y(estimateRub.high)}
                    stroke={EST}
                    strokeWidth={2}
                  />
                  <line
                    x1={xn - cap}
                    x2={xn + cap}
                    y1={y(estimateRub.low)}
                    y2={y(estimateRub.low)}
                    stroke={EST}
                    strokeWidth={2}
                  />
                  <circle
                    cx={xn}
                    cy={y(estimateRub.median)}
                    r={4}
                    fill={EST}
                    stroke="white"
                    strokeWidth={1.5}
                  />
                  <text
                    x={xn - 8}
                    y={y(estimateRub.high) - 6}
                    fontSize={11}
                    fontWeight={700}
                    textAnchor="end"
                    fill={EST}
                  >
                    Оценка {fmt(estimateRub.median)}
                  </text>
                </Group>
              );
            })()}

          {/* Ваша цена (факт) */}
          {facts.length > 1 && (
            <LinePath
              data={facts}
              x={(d) => x(d.date)}
              y={(d) => y(d.fact)}
              stroke={FACT}
              strokeWidth={2.5}
            />
          )}
          {facts.map((d, i) => (
            <circle key={i} cx={x(d.date)} cy={y(d.fact)} r={3} fill={FACT} />
          ))}

          {tooltipData && activeX !== null && (
            <Group>
              <line
                x1={activeX}
                x2={activeX}
                y1={0}
                y2={yMax}
                stroke={axisColor}
                strokeDasharray="4 4"
                strokeOpacity={0.35}
              />
              {tooltipData.estimate != null && (
                <circle
                  cx={activeX}
                  cy={y(tooltipData.estimate)}
                  r={4.5}
                  fill={EST}
                  stroke="white"
                  strokeWidth={1.5}
                />
              )}
              {tooltipData.fact != null && (
                <circle
                  cx={activeX}
                  cy={y(tooltipData.fact)}
                  r={4.5}
                  fill={FACT}
                  stroke="white"
                  strokeWidth={1.5}
                />
              )}
            </Group>
          )}

          {/* Маркер «сейчас» с текущей ценой */}
          {(() => {
            const last = pts[pts.length - 1];
            const yv = last.fact ?? last.estimate;
            if (yv == null) return null;
            const c = last.fact != null ? FACT : EST;
            return (
              <>
                <circle
                  cx={x(last.date)}
                  cy={y(yv)}
                  r={5.5}
                  fill={c}
                  stroke="white"
                  strokeWidth={2}
                />
                <text
                  x={x(last.date)}
                  y={y(yv) - 12}
                  fontSize={11}
                  fontWeight={700}
                  textAnchor="end"
                  fill={c}
                >
                  {fmt(yv)}
                </text>
              </>
            );
          })()}

          <AxisBottom
            top={yMax}
            scale={x}
            numTicks={6}
            tickFormat={(v) =>
              new Date(Number(v)).toLocaleDateString("ru-RU", {
                month: "short",
              })
            }
            tickLabelProps={() => ({
              fill: axisColor,
              fontSize: 11,
              textAnchor: "middle",
            })}
          />
          <AxisLeft
            scale={y}
            tickFormat={fmt}
            tickLabelProps={() => ({
              fill: axisColor,
              fontSize: 11,
              textAnchor: "end",
            })}
          />
          <rect
            width={xMax}
            height={yMax}
            fill="transparent"
            onMouseMove={handleMouseMove}
            onMouseLeave={hideTooltip}
          />
        </Group>
      </svg>

      <div className="flex gap-4 justify-center text-xs text-foreground-500 mt-1">
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-0.5 bg-emerald-500" /> Ваша цена
        </span>
        {(estimates.length > 0 || estimateRub) && (
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-0.5 bg-blue-500" /> Оценка
          </span>
        )}
        {estimateCorridor.length > 0 && (
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-2 rounded-sm bg-blue-500/20" />
            Прогноз от–до
          </span>
        )}
      </div>

      {tooltipData && (
        <Tooltip
          left={tooltipLeft}
          top={tooltipTop}
          style={{
            ...defaultStyles,
            backgroundColor: "rgba(0,0,0,0.7)",
            color: "white",
            borderRadius: 8,
            border: "none",
            padding: "8px 12px",
            fontSize: 13,
          }}
        >
          <div className="min-w-48">
            <div className="mb-1 font-semibold">
              {tooltipData.date.toLocaleDateString("ru-RU", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
            </div>
            <div className="grid grid-cols-[auto_1fr_auto] items-center gap-x-2 gap-y-1">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: EST }}
              />
              <span className="text-white/70">Оценка</span>
              <strong>{fmtMaybe(tooltipData.estimate)}</strong>

              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: FACT }}
              />
              <span className="text-white/70">Ваша цена</span>
              <strong>{fmtMaybe(tooltipData.fact)}</strong>

              <span />
              <span className="text-white/70">Прогноз от–до</span>
              <span>{forecastRange}</span>
            </div>
          </div>
        </Tooltip>
      )}
    </div>
  );
}
